import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";

import { BetaChip } from "../../components/BetaChip";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import CompetitorMapTab from "./CompetitorMapTab";
import { CONTRACTS_KEY } from "./constants";
import { fetchContractedProperties } from "./services";
import { ContractedProperty } from "./types";

/**
 * Choosing competitors, and seeing what they charge.
 *
 * ⚠️ The property picker sits **above** the map rather than inside it: it scopes the
 * whole page, and a picker per panel would let two of them disagree.
 *
 * ⚠️ The map is mounted only once a property is chosen. MapLibre initialises against
 * its container, and one that is still zero-size comes back blank.
 */
const CompetitorSelectionPage = () => {
  const intl = useIntl();

  const [propertyId, setPropertyId] = useState<number | null>(null);

  const contracts = useQuery(CONTRACTS_KEY, fetchContractedProperties);

  const property: ContractedProperty | undefined = useMemo(
    () => contracts.data?.find((item) => item.propertyId === propertyId),
    [contracts.data, propertyId]
  );

  // The default property, once. Picking it in an effect rather than in `useState`
  // because the list arrives after the first render.
  useEffect(() => {
    if (propertyId === null && contracts.data?.length) {
      setPropertyId(contracts.data[0]?.propertyId ?? null);
    }
  }, [contracts.data, propertyId]);

  if (contracts.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ⚠️ Error before empty, because a failed request also has no data. Checked the
  // other way round, a contracts call that 500d told the customer "you have no
  // contract" — a statement about their account, not about the request — and the
  // error alert further down was unreachable behind this early return.
  if (contracts.isError) {
    return (
      <Alert severity="error">
        <FormattedMessage id="COMPETITOR_SELECTION_LOAD_ERROR" />
      </Alert>
    );
  }

  if (!contracts.data?.length) {
    return (
      <Alert severity="info">
        <FormattedMessage id="COMPETITOR_SELECTION_NO_CONTRACT" />
      </Alert>
    );
  }

  return (
    // ⚠️ `width: 100%` is load-bearing. `ConfigurationView` wraps every page in
    // `Layout/utils.Row`, a `display: flex; flex-direction: row` box that gives its
    // child no width — so a `Stack` sized by its content shrinks to whatever the
    // filter bar needs, which is about a third of the viewport. `minWidth: 0` on top
    // of it so the map and the wide tables inside can scroll rather than forcing the
    // flex item wider than the page.
    <Stack spacing={2} sx={{ width: "100%", minWidth: 0, paddingTop: "16px" }}>
      {/* ⚠️ The page's own title, because nothing above it supplies one: this renders
          as a CUSTOM configuration page, which — unlike a TABLE — gets no `TableTitle`
          from the router. Without it the first thing on screen is a filter bar. */}
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography variant="h5">
          <FormattedMessage id="COMPETITOR_SELECTION" />
        </Typography>
        {/* Not `dense`: that height exists to fit a menu row, and beside an h5 it
            would read as a typo. Same component either way. */}
        <BetaChip />
      </Stack>

      {/* ⚠️ On its own row, above the tabs. The same 12-column grid as the panels
          below, so the left edges line up at every breakpoint rather than by a width
          that would agree only until the spacing changed. */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 9 }}>
          <Box sx={{ minWidth: 280, maxWidth: 480 }}>
            <CustomAutocomplete
              label={intl.formatMessage({
                id: "COMPETITOR_SELECTION_PROPERTY",
              })}
              options={contracts.data}
              // null only before a property is chosen; MUI's `disableClearable` typing forbids it
              value={property as ContractedProperty}
              disableClearable
              getOptionLabel={(option: ContractedProperty) =>
                option.propertyName
              }
              isOptionEqualToValue={(
                option: ContractedProperty,
                selected: ContractedProperty
              ) => option.propertyId === selected.propertyId}
              onChange={(_event: unknown, next: ContractedProperty | null) =>
                setPropertyId(next ? next.propertyId : null)
              }
            />
          </Box>
        </Grid>
      </Grid>

      {property && <CompetitorMapTab property={property} />}
    </Stack>
  );
};

export default CompetitorSelectionPage;
